/**
 * Footer -- Compact "about & legal" block at the bottom of AWENI Home.
 *
 * Layout (mobile-first, 2026-04-18):
 *   [ About · Contact · Privacy · Terms · GDPR ]
 *   [ Made with care in 5 languages. ©2026 Learnature-ly. ]
 *   [ v1.1.0  ·  Check for updates  (status) ]
 *
 * The links point to the WordPress/Learnature-ly canonical pages so there's
 * one URL per content across web and future native apps.
 */

import type { AweniContext } from '../context/AweniContext';
import { updateManager } from '../services/UpdateManager';

const APP_VERSION = '1.1.0';

// Simplified footer (2026-04-19): pointing 5 different labels to the same
// Settings page was confusing ("nothing happens when I click X"). Keep
// About (opens Settings with About auto-expanded) and Contact (mailto, no
// navigation). Privacy/Terms/GDPR will get their own proper sections once
// the Astro migration ships.
const LINKS: Array<{ labelKey: string; fallback: string; href: string }> = [
  { labelKey: 'footer_about', fallback: 'About', href: '#/more/settings?open=about' },
  { labelKey: 'footer_contact', fallback: 'Contact', href: 'mailto:contact@learnature-ly.com' },
];

function translate(t: (k: string) => string, key: string, fallback: string): string {
  const v = t(key);
  return v === key ? fallback : v;
}

export function mountFooter(container: HTMLElement, ctx: AweniContext): void {
  const t = ctx.i18n.t.bind(ctx.i18n);

  // Same-tab navigation (2026-04-19). target="_blank" used to be the default
  // but uBlock and similar extensions strip/rewrite it to same-tab anyway,
  // resulting in inconsistent behavior. The destination /about/ has a fixed
  // green "← Back to AWENI" banner so users always have a one-tap return.
  const linksHtml = LINKS.map(
    (l) =>
      `<a href="${l.href}" style="color:var(--text-secondary);text-decoration:none;padding:2px 6px;white-space:nowrap;">${translate(t, l.labelKey, l.fallback)}</a>`,
  ).join('<span style="color:var(--border);">·</span>');

  const tagline = translate(
    t,
    'footer_tagline',
    'AWENI — your family assistant. Family-safe, mobile-first, 5 languages.',
  );

  container.innerHTML = `
    <div style="padding:28px 16px 96px;font-size:12px;color:var(--text-secondary);max-width:480px;margin:0 auto;text-align:center;">
      <nav aria-label="${translate(t, 'footer_legal_nav', 'Site links')}"
           style="display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:4px;line-height:2;opacity:0.85;">
        ${linksHtml}
      </nav>
      <p style="margin:10px 0 6px;opacity:0.7;line-height:1.5;">${tagline}</p>
      <p style="margin:0 0 14px;opacity:0.55;">&copy; 2026 Learnature-ly. ${translate(t, 'footer_rights', 'All rights reserved.')}</p>
      <div style="opacity:0.5;">
        <span>AWENI v${APP_VERSION}</span>
        <span style="margin:0 6px;">·</span>
        <button id="aweni-check-update-btn"
                style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:12px;text-decoration:underline;padding:0;">
          \u{1F504} ${t('footer_check_updates')}
        </button>
        <div id="aweni-update-status" style="margin-top:4px;font-size:11px;"></div>
      </div>
    </div>
  `;

  const btn = container.querySelector('#aweni-check-update-btn') as HTMLElement;
  const status = container.querySelector('#aweni-update-status') as HTMLElement;

  if (btn) {
    btn.addEventListener('click', async () => {
      btn.textContent = t('common_loading');
      status.textContent = '';

      try {
        const manifest = await updateManager.checkForUpdates();

        if (manifest) {
          status.innerHTML = `<span style="color:#10B981;">${t('footer_update_available').replace('{version}', manifest.app_version)}</span>`;
          btn.textContent = t('footer_tap_to_update');
          btn.onclick = async () => {
            // Clear all caches
            if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
            // Update SW
            const reg = await navigator.serviceWorker?.getRegistration();
            if (reg) await reg.update();
            // Hard refresh
            window.location.reload();
          };
        } else {
          status.innerHTML = `<span style="color:#10B981;">\u2713 ${t('settings_up_to_date')}</span>`;
          btn.textContent = `\u{1F504} ${t('footer_check_updates')}`;
        }
      } catch {
        status.innerHTML = `<span style="color:#EF4444;">${t('common_error')}</span>`;
        btn.textContent = `\u{1F504} ${t('footer_check_updates')}`;
      }
    });
  }
}

/**
 * Utility: perform a hard refresh clearing all caches.
 * Can be called from Settings > About > Check for updates.
 */
export async function performHardRefresh(): Promise<void> {
  // Clear all caches
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  // Update SW
  const reg = await navigator.serviceWorker?.getRegistration();
  if (reg) await reg.update();
  // Hard refresh
  window.location.reload();
}
